import { markRaw } from 'vue'
import ClientNameColumn from './ClientNameColumn.vue'
import ClientLocationColumn from './ClientLocationColumn.vue'
import ClientRecruitersColumn from './ClientRecruitersColumn.vue'
import ClientPlacementsColumn from './ClientPlacementsColumn.vue'
import ClientAddedColumn from './ClientAddedColumn.vue'
import ClientOwnersColumn from './ClientOwnersColumn.vue'
import {
  ClientRecruitersColumnFragment,
  ClientRecruitersColumnFragmentDoc,
  ClientLocationColumnFragment,
  ClientLocationColumnFragmentDoc,
  ClientNameColumnFragment,
  ClientNameColumnFragmentDoc,
  ClientPlacementsColumnFragment,
  ClientPlacementsColumnFragmentDoc,
  ClientAddedColumnFragment,
  ClientAddedColumnFragmentDoc,
  ClientOwnersColumnFragment,
  ClientOwnersColumnFragmentDoc,
  SortOrder,
  CompanyClientOrderByColumn,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import { toOwnerSelection } from '@/components/Owners/fragments'

const columns: ColumnMaps = {
  ClientNameColumn: {
    fragment: ClientNameColumnFragmentDoc,
    component: markRaw(ClientNameColumn),
    columnDef: {
      title: $t('Client'),
      key: 'ClientNameColumn',
      orderAble: true,
      ignoreClickForRowSelection: true,
      renderUsing: {
        component: markRaw(ClientNameColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Client name',
        extraction: (row: ClientNameColumnFragment) => row.company.name,
      },
    ],
    sort: [
      {
        name: `${CompanyClientOrderByColumn.ClientName}_${SortOrder.Asc}`,
        orderByColumn: CompanyClientOrderByColumn.ClientName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Client'),
        secondaryText: $t('A - Z'),
        title: $t('Order by client name'),
      },
      {
        name: `${CompanyClientOrderByColumn.ClientName}_${SortOrder.Desc}`,
        orderByColumn: CompanyClientOrderByColumn.ClientName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Client'),
        secondaryText: $t('Z - A'),
        title: $t('Order by client name'),
      },
    ],
  },
  ClientLocationColumn: {
    fragment: ClientLocationColumnFragmentDoc,
    component: markRaw(ClientLocationColumn),
    columnDef: {
      title: $t('Location'),
      key: 'ClientLocationColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(ClientLocationColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Country',
        extraction: (row: ClientLocationColumnFragment) =>
          row.company.address?.country?.name ?? row.company.market,
      },
      {
        title: 'Region',
        extraction: (row: ClientLocationColumnFragment) => row.company.region?.name ?? '',
      },
    ],
    sort: [
      {
        name: `${CompanyClientOrderByColumn.ClientLocation}_${SortOrder.Asc}`,
        orderByColumn: CompanyClientOrderByColumn.ClientLocation,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Location'),
        secondaryText: $t('A - Z'),
        title: $t('Order by location'),
      },
      {
        name: `${CompanyClientOrderByColumn.ClientLocation}_${SortOrder.Desc}`,
        orderByColumn: CompanyClientOrderByColumn.ClientLocation,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Location'),
        secondaryText: $t('Z - A'),
        title: $t('Order by location'),
      },
    ],
  },
  ClientRecruitersColumn: {
    fragment: ClientRecruitersColumnFragmentDoc,
    component: markRaw(ClientRecruitersColumn),
    columnDef: {
      title: $t('Linked agencies'),
      key: 'ClientRecruitersColumn',
      orderAble: true,
      ignoreClickForRowSelection: true,
      renderUsing: {
        component: markRaw(ClientRecruitersColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Linked staffing agencies',
        extraction: (row: ClientRecruitersColumnFragment) =>
          row.linkedStaffingAgencies.data.map((relation) => relation.recruiter.name).join(', '),
      },
      {
        title: 'Linked staffing agency count',
        extraction: (row: ClientRecruitersColumnFragment) =>
          `${row.linkedStaffingAgencies.paginatorInfo.total}`,
      },
    ],
    sort: [
      {
        name: `${CompanyClientOrderByColumn.LinkedStaffingAgencies}_${SortOrder.Desc}`,
        orderByColumn: CompanyClientOrderByColumn.LinkedStaffingAgencies,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Linked agencies'),
        secondaryText: $t('High to low'),
        title: $t('Order by linked staffing agencies'),
      },
      {
        name: `${CompanyClientOrderByColumn.LinkedStaffingAgencies}_${SortOrder.Asc}`,
        orderByColumn: CompanyClientOrderByColumn.LinkedStaffingAgencies,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Linked agencies'),
        secondaryText: $t('Low to high'),
        title: $t('Order by linked staffing agencies'),
      },
    ],
  },
  ClientOwnersColumn: {
    fragment: ClientOwnersColumnFragmentDoc,
    component: markRaw(ClientOwnersColumn),
    columnDef: {
      title: $t('Client owners'),
      key: 'ClientOwnersColumn',
      renderUsing: {
        component: markRaw(ClientOwnersColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Owners',
        extraction: (row: ClientOwnersColumnFragment) =>
          row.owners.map((owner) => toOwnerSelection(owner).label).join(', '),
      },
    ],
  },
  ClientPlacementsColumn: {
    fragment: ClientPlacementsColumnFragmentDoc,
    component: markRaw(ClientPlacementsColumn),
    columnDef: {
      title: $t('Placements | Active'),
      key: 'ClientPlacementsColumn',
      orderAble: true,
      ignoreClickForRowSelection: true,
      renderUsing: {
        component: markRaw(ClientPlacementsColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Placements',
        extraction: (row: ClientPlacementsColumnFragment) => `${row.hiresCount}`,
      },
      {
        title: 'Active placements',
        extraction: (row: ClientPlacementsColumnFragment) => `${row.activeHiresCount}`,
      },
    ],
    sort: [
      {
        name: `${CompanyClientOrderByColumn.Hires}_${SortOrder.Desc}`,
        orderByColumn: CompanyClientOrderByColumn.Hires,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Placements'),
        secondaryText: $t('High to low'),
        title: $t('Order by placements'),
      },
      {
        name: `${CompanyClientOrderByColumn.Hires}_${SortOrder.Asc}`,
        orderByColumn: CompanyClientOrderByColumn.Hires,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Placements'),
        secondaryText: $t('Low to high'),
        title: $t('Order by placements'),
      },
    ],
  },
  ClientAddedColumn: {
    fragment: ClientAddedColumnFragmentDoc,
    component: markRaw(ClientAddedColumn),
    columnDef: {
      title: $t('Client added on'),
      key: 'ClientAddedColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(ClientAddedColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Client added on',
        extraction: (row: ClientAddedColumnFragment) => row.createdAt,
      },
    ],
    sort: [
      {
        name: `${CompanyClientOrderByColumn.ClientSince}_${SortOrder.Desc}`,
        orderByColumn: CompanyClientOrderByColumn.ClientSince,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Client added'),
        secondaryText: $t('New to old'),
        title: $t('Order by date they were added'),
      },
      {
        name: `${CompanyClientOrderByColumn.ClientSince}_${SortOrder.Asc}`,
        orderByColumn: CompanyClientOrderByColumn.ClientSince,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Client added'),
        secondaryText: $t('Old to new'),
        title: $t('Order by date they were added'),
      },
    ],
  },
}

export default columns
